import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  Divider,
  Typography,
  ListItemButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';
import CandlestickChart from './CandlestickChart'; // Import CandlestickChart

interface Ticker {
  id: string;
  ticker: string;
  company_name: string;
  // Add other properties if necessary
}

const TickerSidebar: React.FC = () => {
  const [tickers, setTickers] = useState<Ticker[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Ticker[]>([]);
  const [isFetchingTickers, setIsFetchingTickers] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [addingTickers, setAddingTickers] = useState<Set<string>>(new Set());
  const [removingTickers, setRemovingTickers] = useState<Set<string>>(new Set());

  const [selectedTickerId, setSelectedTickerId] = useState<string | null>(null);
  const [selectedTickerData, setSelectedTickerData] = useState<any>(null);
  const [loadingChart, setLoadingChart] = useState<boolean>(false);
  const [errorLoadingChart, setErrorLoadingChart] = useState<string | null>(null);

  useEffect(() => {
    fetchUserTickers();
  }, []);

  useEffect(() => {
    if (selectedTickerId) {
      fetchTickerAggregateData(selectedTickerId);
    }
  }, [selectedTickerId]);

  const fetchUserTickers = async () => {
    setIsFetchingTickers(true);
    try {
      const response = await api.get('/user/ticker');
      setTickers(response.data);
    } catch (error) {
      console.error('Error fetching user tickers:', error);
      setError('Failed to fetch your tickers. Please try again.');
    } finally {
      setIsFetchingTickers(false);
    }
  };

  const fetchTickerAggregateData = async (tickerId: string) => {
    setLoadingChart(true);
    setErrorLoadingChart(null);
    try {
      const response = await api.get('/ticker/aggregate', {
        params: { ticker_id: tickerId },
      });
      setSelectedTickerData(response.data);
    } catch (error) {
      console.error('Error fetching ticker aggregate data:', error);
      setErrorLoadingChart('Failed to load chart data. Please try again.');
    } finally {
      setLoadingChart(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const response = await api.get('/ticker/search', {
        params: { query: searchQuery },
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching tickers:', error);
      setError('Failed to search tickers. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddTicker = async (tickerId: string) => {
    setAddingTickers((prev) => new Set(prev).add(tickerId));
    try {
      await api.patch('/user/add-ticker', null, {
        params: { ticker_id: tickerId },
      });
      fetchUserTickers();
      setSearchResults([]);
      setSearchQuery('');
      setSuccess('Ticker added successfully!');
    } catch (error) {
      console.error('Error adding ticker:', error);
      setError('Failed to add ticker. Please try again.');
    } finally {
      setAddingTickers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(tickerId);
        return newSet;
      });
    }
  };

  const handleRemoveTicker = async (tickerId: string) => {
    setRemovingTickers((prev) => new Set(prev).add(tickerId));
    try {
      await api.delete('/user/remove-ticker', {
        params: { ticker_id: tickerId },
      });
      setTickers((prevTickers) => prevTickers.filter((ticker) => ticker.id !== tickerId));
      setSuccess('Ticker removed successfully!');
      if (selectedTickerId === tickerId) {
        setSelectedTickerId(null);
        setSelectedTickerData(null);
      }
    } catch (error) {
      console.error('Error removing ticker:', error);
      setError('Failed to remove ticker. Please try again.');
    } finally {
      setRemovingTickers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(tickerId);
        return newSet;
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearchResults = () => {
    setSearchResults([]);
  };

  const handleTickerClick = (tickerId: string) => {
    setSelectedTickerId(tickerId);
  };

  return (
    <Paper
      sx={{
        height: '85vh',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden', // Prevent overflow
      }}
      elevation={3}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid #ddd', boxSizing: 'border-box' }}>
        <TextField
          fullWidth
          placeholder="Search for tickers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: (
              <IconButton onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
                {isSearching ? <CircularProgress size={24} /> : <SearchIcon />}
              </IconButton>
            ),
          }}
        />
      </Box>

      {searchResults.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              p: 2,
              maxHeight: '30vh',
              overflowY: 'auto',
              boxSizing: 'border-box',
            }}
          >
            <List disablePadding>
              {searchResults.map((ticker) => (
                <ListItem
                  key={ticker.id}
                  divider
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleAddTicker(ticker.id)}
                      disabled={addingTickers.has(ticker.id)}
                    >
                      {addingTickers.has(ticker.id) ? <CircularProgress size={24} /> : <AddIcon />}
                    </IconButton>
                  }
                >
                  <ListItemText primary={ticker.ticker} secondary={ticker.company_name} />
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #ddd',
          flexGrow: 1,
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <List disablePadding>
          {tickers.map((ticker) => (
            <ListItem
              key={ticker.id}
              divider
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={() => handleRemoveTicker(ticker.id)}
                  disabled={removingTickers.has(ticker.id)}
                >
                  {removingTickers.has(ticker.id) ? <CircularProgress size={24} /> : <DeleteIcon />}
                </IconButton>
              }
            >
              <ListItemButton
                selected={selectedTickerId === ticker.id}
                onClick={() => handleTickerClick(ticker.id)}
              >
                <ListItemText primary={ticker.ticker} secondary={ticker.company_name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {selectedTickerId && (
        <Box
          sx={{
            mt: 2,
            width: '100%',
            textAlign: 'center',
            padding: '10px',
            boxSizing: 'border-box',
            flexShrink: 0, // Prevent box from growing/shrinking
            height: '300px', // Fixed height for the chart container
          }}
        >
          <Typography variant="caption" component="div" sx={{ mb: 1 }}>
            {tickers.find((ticker) => ticker.id === selectedTickerId)?.ticker}
          </Typography>
          {loadingChart ? (
            <CircularProgress size={30} />
          ) : (
            selectedTickerData && (
              <Box
                sx={{
                  width: '100%',
                  height: 'calc(100% - 24px)', // Subtract typography height
                  boxSizing: 'border-box',
                }}
              >
                <CandlestickChart data={selectedTickerData} />
              </Box>
            )
          )}
        </Box>
      )}
    </Paper>
  );
};

export default TickerSidebar;
